import type {
    OrderMethodDetail,
    OrderMethodSummary,
    VendorDetail,
    VendorListItem
} from '../../../services/vendors';

// Two layers, because "cannot be ordered from" now lives per method and rolls
// up to the vendor. The server's `isUsable` decides; these only explain why.

export type MethodBlock = 'retired' | 'no_recipients';

// Legacy rows only: the API now refuses an email method with no recipients.
export function methodBlockReason(
    method: Pick<OrderMethodDetail, 'isActive' | 'kind' | 'poRecipients'>
): MethodBlock | null {
    if (!method.isActive) return 'retired';
    if (method.kind === 'email' && method.poRecipients.length === 0) return 'no_recipients';
    return null;
}

export function methodBlockLabel(block: MethodBlock): string {
    return block === 'retired' ? 'Retired' : 'No recipients';
}

// Server-authoritative. `methodBlockReason` is only consulted for the wording,
// so the two can never disagree about whether a method is usable.
export function isMethodUsable(method: Pick<OrderMethodDetail, 'isUsable'>): boolean {
    return method.isUsable;
}

export type VendorBlock = 'retired' | 'no_methods' | 'no_usable_method';

export function vendorDispatchBlock(
    vendor: Pick<VendorListItem, 'isActive'> & { orderMethods: OrderMethodSummary }
): VendorBlock | null {
    if (!vendor.isActive) return 'retired';
    if (vendor.orderMethods.total === 0) return 'no_methods';
    if (vendor.orderMethods.usable === 0) return 'no_usable_method';
    return null;
}

export function vendorBlockLabel(block: VendorBlock): string {
    if (block === 'retired') return 'Retired';
    if (block === 'no_methods') return 'No ordering methods';
    return 'No usable method';
}

// An ACTIVE vendor that cannot be ordered from is the dangerous case: it looks
// available and silently drops automatic ordering. A retired one is deliberate.
export function undispatchableVendors(vendors: readonly VendorListItem[]): VendorListItem[] {
    return vendors.filter((vendor) => {
        const block = vendorDispatchBlock(vendor);
        return block === 'no_methods' || block === 'no_usable_method';
    });
}

// Lets the detail reuse the list's rule instead of restating it. `usable`
// counts the server's own verdict, never a re-derivation.
export function toRollup(vendor: VendorDetail): {
    isActive: boolean;
    orderMethods: OrderMethodSummary;
} {
    const methods = vendor.orderMethods;
    return {
        isActive: vendor.isActive,
        orderMethods: {
            total: methods.length,
            email: methods.filter((m) => m.kind === 'email').length,
            external: methods.filter((m) => m.kind === 'external').length,
            active: methods.filter((m) => m.isActive).length,
            usable: methods.filter((m) => m.isUsable).length
        }
    };
}
